import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, AlertTriangle, Activity } from "lucide-react";
import { api } from "../api";
import { Panel, PageHead, Loading, LoadError, StatusBadge, PriorityBadge, EmptyState, fmtDate, fmtDateTime } from "../ui";

const StatCard = ({ label, value, testid }) => (
    <Panel className="p-5" data-testid={testid}>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#64716D]">{label}</p>
        <p className="mt-2 text-3xl font-bold text-[#F7F5EF]">{value}</p>
    </Panel>
);

export const AdminDashboard = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api
            .get("/admin/dashboard")
            .then((res) => setData(res.data))
            .catch(() => setError("Could not load the dashboard. Please refresh."));
    }, []);

    if (error) return <LoadError message={error} />;
    if (!data) return <Loading />;

    const { stats, recent_cases: recentCases, attention, recent_activity: activity } = data;

    return (
        <div data-testid="admin-dashboard">
            <PageHead
                title="Operations Dashboard"
                subtitle="Live overview of every enquiry and case in the pipeline."
            />

            <div className="grid grid-cols-2 xl:grid-cols-5 gap-4" data-testid="dashboard-stat-cards">
                <StatCard label="New" value={stats.new} testid="stat-card-new" />
                <StatCard label="Active" value={stats.active} testid="stat-card-active" />
                <StatCard label="Consultation pending" value={stats.consultation_pending} testid="stat-card-consultation-pending" />
                <StatCard label="Travel planning" value={stats.travel_planning} testid="stat-card-travel-planning" />
                <StatCard label="Total cases" value={stats.total} testid="stat-card-total" />
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                <Panel className="lg:col-span-2 overflow-hidden">
                    <div className="flex items-center justify-between px-5 pt-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            Recent cases
                        </h2>
                        <Link
                            to="/admin/cases"
                            data-testid="dashboard-view-all-cases"
                            className="text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] transition-colors"
                        >
                            View all →
                        </Link>
                    </div>
                    {recentCases.length === 0 ? (
                        <EmptyState icon={Inbox} title="No cases yet" hint="New enquiries will appear here." />
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-left" data-testid="dashboard-recent-table">
                                <thead>
                                    <tr className="border-y border-[#1A2E2A] bg-[#0E1A17]">
                                        {["Case", "Patient", "Country", "Status", "Priority", "Created"].map((h) => (
                                            <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentCases.map((c) => (
                                        <tr
                                            key={c.case_id}
                                            data-testid={`case-row-${c.case_id}`}
                                            className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors"
                                        >
                                            <td className="px-4 py-2.5 text-xs font-mono tracking-tight">
                                                <Link to={`/admin/cases/${c.case_id}`} data-testid={`case-link-${c.case_id}`} className="text-emerald-300 hover:text-emerald-200">
                                                    {c.case_id}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-[#F7F5EF] font-medium whitespace-nowrap">{c.patient_name}</td>
                                            <td className="px-4 py-2.5 text-sm text-[#A3B8B0] whitespace-nowrap">{c.country || "—"}</td>
                                            <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                                            <td className="px-4 py-2.5"><PriorityBadge priority={c.priority} /></td>
                                            <td className="px-4 py-2.5 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(c.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Panel>

                <Panel className="p-5">
                    <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-amber-400">
                        <AlertTriangle className="w-4 h-4" /> Needs attention
                    </h2>
                    {attention.length === 0 ? (
                        <p className="mt-4 text-sm text-[#64716D]" data-testid="attention-empty">
                            Nothing urgent — the pipeline is calm.
                        </p>
                    ) : (
                        <ul className="mt-4 space-y-3" data-testid="attention-list">
                            {attention.map((c) => (
                                <li key={c.case_id}>
                                    <Link
                                        to={`/admin/cases/${c.case_id}`}
                                        data-testid={`attention-item-${c.case_id}`}
                                        className="block rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3 hover:border-[#0B5148] transition-colors"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-xs font-mono text-emerald-300">{c.case_id}</span>
                                            <PriorityBadge priority={c.priority} />
                                        </div>
                                        <p className="mt-1.5 text-sm font-medium text-[#F7F5EF] truncate">{c.patient_name}</p>
                                        <p className="text-xs text-[#64716D] mt-0.5">
                                            {c.assigned_coordinator ? c.assigned_coordinator.name : "Unassigned"}
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>
            </div>

            <Panel className="mt-6 p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                    <Activity className="w-4 h-4" /> Recent activity
                </h2>
                {activity.length === 0 ? (
                    <p className="mt-4 text-sm text-[#64716D]" data-testid="activity-empty">
                        Activity will appear as your team works cases.
                    </p>
                ) : (
                    <ul className="mt-4 space-y-3" data-testid="activity-feed">
                        {activity.map((a, i) => (
                            <li key={`${a.case_id}-${i}`} className="flex items-start gap-3">
                                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500/80" />
                                <div className="min-w-0">
                                    <p className="text-sm text-[#F7F5EF]">
                                        {a.event}
                                        {a.case_id && (
                                            <>
                                                {" · "}
                                                <Link to={`/admin/cases/${a.case_id}`} className="text-xs font-mono text-emerald-300 hover:text-emerald-200">
                                                    {a.case_id}
                                                </Link>
                                            </>
                                        )}
                                    </p>
                                    <p className="text-xs text-[#64716D]">
                                        {a.user_name} · {fmtDateTime(a.created_at)}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Panel>
        </div>
    );
};
